import cx from 'classnames';
import { useRouter } from 'next/router';
import useTranslation from 'next-translate/useTranslation';

import { getBrandHistory } from '../../../../../services/brand-history';
import { TsearchCategoryName } from '../../../../../utils/get-category-names';
import Trustpilot from '../../../../modules/trustpilot';
import BrandHistory from '../../../../modules/v2/brand-history';
import Breadcrumbs from '../../../../modules/v2/breadcrumbs';
import ValueProposition from '../../../../modules/value-proposition';
import { useBreadcrumbsType } from '../../../vehicle/hooks/use-breadcrumbs';
import LastEntries from '../last-entries';
import LastVehicles from '../last-vehicles';

import ModelsList from './model-list';
import ModelsTitle from './models-title';
import ModelsSchema from './seo';
import TopModels from './top-models';
import SEOContent from './seo-content-section';
import dynamic from 'next/dynamic';

const TrustBox = dynamic(() => import('../../../../modules/trustpilot/new'), { ssr: false });

type ModelsPageType = {
  breadcrumbs: ReturnType<useBreadcrumbsType>;
  info: TsearchCategoryName | null;
  selection: { category: TCategorySelection };
  vehicle: TCarInfo;
  groups: TCategories;
  stock: TResultAPIStocksItem[];
  models: TResultAPICarsBySlug;
  lastEntries: TProductListItem[];
  vehicles: TVehicleCard[];
  translatedSlugs: any;
};

const ModelsPage = ({
  breadcrumbs,
  info,
  selection,
  vehicle,
  groups,
  stock,
  models,
  lastEntries,
  vehicles,
  translatedSlugs,
}: ModelsPageType) => {
  const { t } = useTranslation();
  const { locale = 'en' } = useRouter();
  const brandHistory = getBrandHistory({
    locale: locale,
    slug: vehicle.brand.alias,
  });

  return (
    <>
      <ModelsSchema
        breadcrumbs={breadcrumbs}
        info={info}
        selection={selection}
        vehicle={vehicle}
        translatedSlugs={translatedSlugs}
        stock={stock}
      />
      <Breadcrumbs
        items={breadcrumbs}
        currentPage={
          vehicle && t('common:model.breadrumb', { brand: vehicle.brand.name })
        }
      />
      <ModelsTitle
        groups={groups}
        selection={selection}
        vehicle={vehicle}
        stock={stock}
        info={info}
      />
      {!!models?.top_models && (
        <TopModels
          title={t('models:top-models', { brand: vehicle.brand.name })}
          models={models.top_models}
          brand={vehicle.brand.name}
        />
      )}
      <ModelsList models={models} />
      {lastEntries && !!lastEntries.length && (
        <LastEntries products={lastEntries} vehicle={vehicle} />
      )}
      <ValueProposition infoToDisplayInTitle={vehicle.brand?.name} />
      {vehicles && !!vehicles.length && (
        <LastVehicles products={vehicles} vehicle={vehicle} />
      )}
      <section className="w-full bg-blue-10 py-12 lg:py-16 text-justify ">
        {brandHistory && (
          <BrandHistory
            imageURL={vehicle.brand.logo_url}
            {...brandHistory}
            brand={vehicle.brand.name}
            vehicle={vehicle}
            stock={stock && stock[1]?.stock.toLocaleString(locale)}
          />
        )}
        <SEOContent
          withPart={!!selection?.category}
          stock={stock[0]?.stock.toLocaleString(locale)}
          info={info}
          vehicle={vehicle}
        ></SEOContent>
      </section>
      <div className="bg-white">
        <div className={cx('py-8 lg:py-6')}>
          <div className={cx('container', 'mx-auto', 'text-center')}>
            <TrustBox />
          </div>
        </div>
      </div>
    </>
  );
};

export default ModelsPage;
